import { cn } from "@/lib/utils";
import {
	describeImprovement,
	readGrade,
	type ReadabilityBand,
} from "./readabilityGrade";

interface ReadabilityMeterProps {
	/** The text to score. Recomputed on every change, so the bar moves live. */
	text: string;
	/**
	 * The copy this replaces. When given, the meter reports the CHANGE — which
	 * stays meaningful on short sentences where the absolute grade does not.
	 */
	compareTo?: string;
	/** Optional caption, e.g. "Original" / "Your edit". */
	caption?: string;
	className?: string;
}

// Direction-of-change status, so red/green is the correct palette here (the
// Core theme reserves them for trends and status). Tokens, never raw palette
// classes — `--status-*` is what the rest of the app grades against.
const BAND_STYLES: Record<ReadabilityBand, { bar: string; text: string }> = {
	hard: {
		bar: "bg-[hsl(var(--status-error))]",
		text: "text-[hsl(var(--status-error))]",
	},
	ok: {
		bar: "bg-[hsl(var(--status-warning))]",
		text: "text-[hsl(var(--status-warning))]",
	},
	easy: {
		bar: "bg-[hsl(var(--status-success))]",
		text: "text-[hsl(var(--status-success))]",
	},
	// Deliberately neutral: a verdict we cannot stand behind gets no colour.
	unscorable: { bar: "bg-muted-foreground/30", text: "text-muted-foreground" },
};

/**
 * Live readability bar. Fills and turns green as the copy gets simpler, so a
 * specialist editing a suggestion sees the effect of each word immediately
 * instead of guessing and waiting for the backend to re-score on apply.
 *
 * Two rules keep it honest:
 *  - text too short for Flesch-Kincaid shows a grey "too short to score", never
 *    a red verdict it cannot justify;
 *  - when the original is known, the CHANGE is reported, because direction is
 *    meaningful even where the absolute number is not.
 */
export function ReadabilityMeter({
	text,
	compareTo,
	caption,
	className,
}: ReadabilityMeterProps) {
	const reading = readGrade(text);
	const improvement = compareTo ? describeImprovement(compareTo, text) : null;

	if (!reading) {
		return (
			<div className={cn("space-y-1", className)}>
				<div className="flex items-center justify-between text-[11px] text-muted-foreground">
					<span>{caption}</span>
					<span>No text to score</span>
				</div>
				<div className="h-1.5 w-full overflow-hidden rounded-full bg-muted" />
			</div>
		);
	}

	const styles = BAND_STYLES[reading.band];
	const improvementTone =
		improvement?.direction === "better"
			? "text-[hsl(var(--status-success))]"
			: improvement?.direction === "worse"
				? "text-[hsl(var(--status-error))]"
				: "text-muted-foreground";

	return (
		<div className={cn("space-y-1", className)}>
			<div className="flex flex-wrap items-center justify-between gap-x-2 text-[11px]">
				<span className="text-muted-foreground">{caption}</span>
				<span className={cn("font-medium", styles.text)}>
					{reading.reliable
						? `${reading.label} · grade ${reading.grade}`
						: "Too short to score reliably"}
				</span>
			</div>
			<div
				className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
				role="meter"
				aria-valuenow={reading.quality}
				aria-valuemin={0}
				aria-valuemax={100}
				aria-label={
					reading.reliable
						? `Readability: ${reading.label}, grade ${reading.grade}`
						: "Readability: too short to score reliably"
				}
			>
				<div
					className={cn("h-full transition-all duration-300", styles.bar)}
					style={{ width: `${reading.quality}%` }}
				/>
			</div>
			{/* The honest success signal: direction beats an absolute the formula
			    cannot deliver on one short sentence. */}
			{improvement && (
				<div className={cn("text-[11px] font-medium", improvementTone)}>
					{improvement.summary}
				</div>
			)}
		</div>
	);
}
